import React, {useState, useRef, useEffect} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Box,
    Typography,
} from "@mui/material";
import {QRCodeSVG} from "qrcode.react";
import QsiIcon from '../images/QSI-BW-Icon.jpg';

const OtpDialog = ({open, onClose, onSubmit, otpAuthUrl, showQrCode}) => {
    const [otp, setOtp] = useState(Array(6).fill(""));
    const inputRefs = useRef([]);
    const [inputDisabled, setInputDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [focusFirstInput, setFocusFirstInput] = useState(false);

    useEffect(() => {
        if (focusFirstInput) {
            inputRefs.current[0]?.focus();
            setFocusFirstInput(false);
        }
    }, [focusFirstInput]);

    const handlePaste = (e, currentIndex) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        // Extract only digits
        const digits = pastedData.replace(/\D/g, "");
        if (!digits) {
            return;
        }

        let newOtp = [...otp];
        for (let i = 0; i < digits.length && currentIndex < 6; i++) {
            newOtp[currentIndex] = digits[i];
            currentIndex++;
        }
        setOtp(newOtp);

        if (currentIndex >= 6) {
            handleSubmit(newOtp);
        } else {
            inputRefs.current[currentIndex]?.focus();
        }
    };
    
    const handleChange = (e, index) => {
        const value = e.target.value;
        let zeroOrOne = /^\d$/;
        let oneOrMore = /^\d+$/;
        const newOtp = [...otp];
        if (zeroOrOne.test(value)) {
            newOtp[index] = value;
            setOtp(newOtp);
            incrementIndexRef(index, newOtp);
        } else if (oneOrMore.test(value)) {
            newOtp[index] = value.slice(-1);
            setOtp(newOtp);
            incrementIndexRef(index, newOtp);
        }
        if (index === 5 && value) {
            handleSubmit(newOtp);
        }
    };
    const incrementIndexRef = (index) => {
        if (index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    }

    const decrementIndexRef = (index) => {
        if (index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            const newOtp = [...otp];
            newOtp[index] = "";
            setOtp(newOtp);
            decrementIndexRef(index);
        }
    };

    const handleSubmit = async (otpArray) => {
        setInputDisabled(true);
        setErrorMessage(""); // Reset error message
        const enteredOtp = otpArray.join("");
        try {
            const response = await onSubmit(enteredOtp);
            if(response?.data?.retryRequired === true){
                setErrorMessage(response?.data?.message);
            }
            else if (response?.status === 200) {
                handleOnClose(response);
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "An unexpected error occurred. Please try again.");
        } finally {
            setInputDisabled(false);
            setFocusFirstInput(true);
            setOtp(Array(6).fill(""));
        }
    };

    function handleOnClose(response) {
        setErrorMessage(""); // Reset error message
        setOtp(Array(6).fill(""));
        onClose(response);
    }

    return (
        <Dialog open={open} onClose={handleOnClose} maxWidth="md">
            <DialogTitle variant="h6" align="center">
                {showQrCode ? "Please scan the QR Code in your Authenticator App." : "Please enter the two factor code from your Authenticator App."}
            </DialogTitle>
            <DialogContent sx={{overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                {showQrCode &&
                    (
                        <>
                            <Box
                            sx={{
                                backgroundColor: 'white',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: 1,
                                borderRadius: 1,
                                mb: 2
                            }}
                            >
                                <QRCodeSVG
                                    value={otpAuthUrl}
                                    size={256}
                                    level={"Q"}
                                    imageSettings={{src: QsiIcon, height: 72, width: 72, excavate: false}}
                                />
                            </Box>
                            <Typography variant="body1" align="center" display="flex" gutterBottom>
                                Then enter the 6 digit passcode below.
                            </Typography>
                        </>
                    )}
                <Box display="flex" justifyContent="center">
                    {[0, 1, 2, 3, 4, 5].map(index => GetTextField(index))}
                </Box>
                {errorMessage && (
                    <Typography color="error" variant="body2" mt={2}>
                        {errorMessage}
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );

    function GetTextField(index) {
        return (
            <TextField
                key={index}
                autoFocus={index === 0}
                inputRef={(ref) => (inputRefs.current[index] = ref)}
                margin="dense"
                type="text"
                disabled={inputDisabled}
                sx={{
                    mx: {xs: 0.4, sm: 1},
                    '& .MuiInputBase-input': {
                        height: "50px",
                        width: {xs: "40px", sm: "50px"},
                        textAlign: 'center',
                        padding: 0,
                    },
                }}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                value={otp[index]}
            />
        );
    }
};

export default OtpDialog;
